import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import * as Sentry from "@sentry/react";
import ConfirmDialog from "../common/ConfirmDialog";
import { useApi } from "../../hooks/useApi";
import { apiErrorMessage } from "../../utils/apiError";
import { useLatest } from "../../hooks/useLatest";
import { balancedBoldMarkers, BOLD_MARKER, stripBoldMarkers, stripEmptyBoldPairs, toggleBold } from "../../utils/boldMarkers";
import { ConclusionFacts, ConclusionFactsView, CONCLUSION_PARAGRAPH_MAX_LENGTH } from "../../types/evidence";

// The stored conclusion is four paragraphs; only the two between the fixed denial and the fixed
// closing paragraph are the agent's to write.
const EDITABLE_LABELS = ["Facts of the transaction", "Why the dispute fails"] as const;

type FactRow = { label: string; value: string; mono?: boolean; sub?: string };
type FactGroup = { heading: string; rows: FactRow[] };

// A fact the document did not prove is an absent key, so a row is skipped rather than shown empty.
function factGroups(facts: ConclusionFacts): FactGroup[] {
  const group = (heading: string, rows: (FactRow | null)[]): FactGroup =>
    ({ heading, rows: rows.filter((row): row is FactRow => Boolean(row)) });
  const row = (label: string, value: string | undefined, extra?: Omit<FactRow, "label" | "value">) =>
    (value ? { label, value, ...extra } : null);

  return [
    group("Order", [
      row("Merchant", facts.merchantName),
      row("Order", facts.orderNumber),
      row("Placed", facts.orderDate),
      row("Amount", facts.amount),
      row("Reason", facts.reasonLabel),
      row("Prior orders", facts.priorOrders && "An earlier order is in the record"),
    ]),
    group("Customer", [
      row("Name", facts.customerName),
      row("Ships to", facts.shippingAddress),
      row("Checkout IP", facts.ip?.address, {
        sub: facts.ip && `same ${facts.ip.matchesBilling} as billing`,
      }),
    ]),
    group("Shipment", [
      row("Carrier", facts.tracking?.carrier),
      row("Tracking", facts.tracking?.number, { mono: true }),
      row("Shipped", facts.tracking?.shippedOn),
      row("Delivered", facts.tracking?.deliveredOn),
    ]),
    group("Digital proof", [
      row("Account activity", facts.digitalProof?.accountActivity ? "The account log is in the record" : undefined),
      row("Product usage", facts.digitalProof?.productUsage ? "Proof of use is in the record" : undefined),
      row("Email log", facts.digitalProof?.emailActivity ? "The email log is in the record" : undefined),
      row("Email opened", facts.digitalProof?.emailOpened ? "The email log shows it opened" : undefined),
      row("Account created", facts.digitalProof?.accountCreatedOn),
      row("Last login", facts.digitalProof?.lastLoginOn),
      row("Cancelled on", facts.digitalProof?.cancelledOn),
    ]),
  ].filter((entry) => entry.rows.length > 0);
}

// Naming what the document does not prove keeps the agent from writing a fact it cannot cite.
function absentFacts(facts: ConclusionFacts): string[] {
  return [
    facts.verifiedChecks?.length ? null : "CVV / AVS result",
    facts.tracking?.deliveredOn ? null : "delivery",
    facts.tracking?.shippedOn ? null : "shipment",
    facts.ip ? null : "IP match",
    facts.noContactAttested ? null : "customer contact",
  ].filter((item): item is string => Boolean(item));
}

export default function ConclusionDialog({ open, draftId, paragraphs, onClose, onSave }: {
  open: boolean;
  draftId: number;
  paragraphs: string[] | null;
  onClose: () => void;
  onSave: (paragraphs: [string, string]) => Promise<void>;
}) {
  const { get } = useApi();
  // Read on open only: a draft refetch while the dialog is up must not discard what the agent typed.
  const latestParagraphs = useLatest(paragraphs);
  const [draftText, setDraftText] = useState<[string, string]>(["", ""]);
  // What the dialog opened on, so Escape and Cancel can tell written work from an untouched form.
  const [openedWith, setOpenedWith] = useState<[string, string]>(["", ""]);
  const [facts, setFacts] = useState<ConclusionFacts | null>(null);
  const [factsError, setFactsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [unclosedBold, setUnclosedBold] = useState<[boolean, boolean]>([false, false]);
  const fieldRefs = useRef<(HTMLTextAreaElement | null)[]>([null, null]);
  // The selection to put back once a bold toggle has re-rendered the field.
  const pendingSelection = useRef<{ index: number; start: number; end: number } | null>(null);
  const latestDraftText = useLatest(draftText);
  const latestOpenedWith = useLatest(openedWith);

  useEffect(() => {
    if (!open) return;
    const existing = latestParagraphs.current ?? [];
    setDraftText([existing[1] ?? "", existing[2] ?? ""]);
    setOpenedWith([existing[1] ?? "", existing[2] ?? ""]);
    setError(null);
    setFactsError(false);
    setConfirmDiscard(false);
    setUnclosedBold([false, false]);
  }, [open, latestParagraphs]);

  // Up to 1200 characters a paragraph, and no recovery once they are gone.
  const edited = draftText[0] !== openedWith[0] || draftText[1] !== openedWith[1];
  const requestClose = () => { if (edited) setConfirmDiscard(true); else onClose(); };

  useEffect(() => {
    if (!open) return;
    let current = true;
    get<ConclusionFactsView>(`/api/evidence-drafts/${draftId}/conclusion/facts`)
      .then((res) => {
        if (!current) return;
        setFacts(res.facts);
        setFactsError(false);
        // The editable text carries the document's bold; take it unless the agent has already started typing.
        const untouched = latestDraftText.current[0] === latestOpenedWith.current[0]
          && latestDraftText.current[1] === latestOpenedWith.current[1];
        if (res.editable && untouched) {
          setDraftText(res.editable);
          setOpenedWith(res.editable);
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        // A failed fetch must not read as "the document proves nothing".
        if (current) setFactsError(true);
      });
    return () => { current = false; };
  }, [open, draftId, get, latestDraftText, latestOpenedWith]);

  useLayoutEffect(() => {
    const pending = pendingSelection.current;
    const field = pending && fieldRefs.current[pending.index];
    if (!pending || !field) return;
    pendingSelection.current = null;
    field.focus();
    field.setSelectionRange(pending.start, pending.end);
  }, [draftText]);

  const setParagraph = (index: number, text: string) => {
    setDraftText((prev) => (index === 0 ? [text, prev[1]] : [prev[0], text]));
    setUnclosedBold((prev) => (index === 0 ? [false, prev[1]] : [prev[0], false]));
  };

  const bold = (index: number) => {
    const field = fieldRefs.current[index];
    if (!field) return;
    const next = toggleBold(draftText[index], field.selectionStart, field.selectionEnd);
    if (next.text === draftText[index]) return;
    pendingSelection.current = { index, start: next.selectionStart, end: next.selectionEnd };
    setParagraph(index, next.text);
  };

  const save = async () => {
    const filled: [string, string] = [stripEmptyBoldPairs(draftText[0]).trim(), stripEmptyBoldPairs(draftText[1]).trim()];
    if (filled.some((text) => !text)) {
      setError("Fill in both paragraphs.");
      return;
    }
    const unclosed: [boolean, boolean] = [!balancedBoldMarkers(filled[0]), !balancedBoldMarkers(filled[1])];
    setUnclosedBold(unclosed);
    if (unclosed.some(Boolean)) {
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(filled);
      onClose();
    } catch (err: any) {
      setError(apiErrorMessage(err, "Failed to save the conclusion"));
    } finally {
      setSaving(false);
    }
  };

  const absent = facts ? absentFacts(facts) : [];

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_, reason) => { if (reason !== "backdropClick") requestClose(); }}
    >
      <DialogTitle>Conclusion</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: "flex", gap: 3, alignItems: "stretch" }}>
          <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The opening denial and the closing paragraph are fixed. Edit the two paragraphs between them.
            </Typography>
            {EDITABLE_LABELS.map((label, index) => (
              <Box key={label}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
                  <Tooltip title={`Bold the selected words (${BOLD_MARKER}text${BOLD_MARKER})`}>
                    <IconButton size="small" aria-label="Bold" onClick={() => bold(index)}>
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  label={label}
                  multiline
                  minRows={5}
                  fullWidth
                  value={draftText[index]}
                  onChange={(event) => setParagraph(index, event.target.value)}
                  inputRef={(element: HTMLTextAreaElement | null) => { fieldRefs.current[index] = element; }}
                  error={unclosedBold[index]}
                  slotProps={{
                    // The limit counts the text without the markers, so the raw field may run past it by them.
                    htmlInput: { maxLength: CONCLUSION_PARAGRAPH_MAX_LENGTH + draftText[index].length - stripBoldMarkers(draftText[index]).length },
                    formHelperText: { component: "div", sx: { display: "flex", justifyContent: "space-between", gap: 2, mx: 0 } },
                  }}
                  helperText={(
                    <>
                      <span>{unclosedBold[index] ? `Close the bold with ${BOLD_MARKER}` : ""}</span>
                      <span>{`${stripBoldMarkers(draftText[index]).length} / ${CONCLUSION_PARAGRAPH_MAX_LENGTH}`}</span>
                    </>
                  )}
                />
              </Box>
            ))}
          </Stack>
          {/* The panel scrolls inside the height the fields set, so a long fact list never grows the dialog. */}
          <Box sx={{ width: 340, flexShrink: 0, position: "relative", alignSelf: "stretch" }}>
            <Box sx={{ position: "absolute", inset: 0, overflowY: "auto", pl: 2.5, pt: 0.75, borderLeft: 1, borderColor: "divider" }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                Proven facts
              </Typography>
              {factsError && <Alert severity="warning" sx={{ mt: 1 }}>Could not load the facts.</Alert>}
              {facts && (
                <>
                  {factGroups(facts).map((group) => (
                    <FactGroupBlock key={group.heading} heading={group.heading}>
                      <Box sx={{
                        display: "grid",
                        gridTemplateColumns: "82px 1fr",
                        columnGap: 1.25,
                        rowGap: 0.5,
                        alignItems: "baseline",
                      }}>
                        {group.rows.map((row) => (
                          <Box key={row.label} sx={{ display: "contents" }}>
                            <Typography variant="caption" color="text.secondary">{row.label}</Typography>
                            <Box sx={{ minWidth: 0 }}>
                              <FactValue value={row.value} mono={row.mono} />
                              {row.sub && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  {row.sub}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </FactGroupBlock>
                  ))}
                  {facts.policiesShown && facts.policiesShown.length > 0 && (
                    <FactGroupBlock heading="Policies shown at checkout">
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {facts.policiesShown.map((policy) => (
                          <Chip
                            key={policy}
                            size="small"
                            variant="outlined"
                            label={policy.charAt(0).toUpperCase() + policy.slice(1)}
                            sx={{ color: "text.secondary" }}
                          />
                        ))}
                      </Box>
                    </FactGroupBlock>
                  )}
                  <FactGroupBlock heading="Sections you may cite">
                    {facts.sectionsPrinted.length === 0
                      ? <Typography variant="body2">None — cite nothing.</Typography>
                      : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {facts.sectionsPrinted.map((section) => (
                            <Chip
                              key={section}
                              size="small"
                              variant="outlined"
                              label={section}
                            />
                          ))}
                        </Box>
                      )}
                  </FactGroupBlock>
                  {absent.length > 0 && (
                    <FactGroupBlock heading="Not in the record">
                      <Typography variant="caption" color="text.secondary">{absent.join(" · ")}</Typography>
                    </FactGroupBlock>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard your conclusion?"
        body="The text you typed has not been saved."
        confirmLabel="Discard"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={onClose}
      />
    </Dialog>
  );
}

function FactGroupBlock({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Box sx={{ mt: 1.75 }}>
      <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.75 }}>{heading}</Typography>
      {children}
    </Box>
  );
}

function FactValue({ value, mono }: { value: string; mono?: boolean }) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: mono ? "0.78rem" : "0.8125rem",
        fontFamily: mono ? "monospace" : undefined,
        fontVariantNumeric: "tabular-nums",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
  );
}
